import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { TranslateService } from '@ngx-translate/core';
import 'rxjs/add/observable/forkJoin';
import { ApiService } from '../api.service';
import { PropertyPatch, PropertyValue, Property } from 'ri-api-client';
import { Helper } from '../helpers/helper';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigService } from '../../../app/config.service';
import { OAuthService } from 'angular-oauth2-oidc';



export interface IPropertyType {
  'id': number;
  'name': string;
  'groupId': number;
  'localizedName': string;
}
export interface IProperty {
  'id': number;
  'modelName': string;
  'used': boolean;
  'atFilters': boolean;
  'atGrouping': boolean;
  'displayName': string;
  'type': IPropertyType;
  'relatedTypes'?: Array<IPropertyType>;
}

@Injectable()
export class SettingService {
  private requestHeaders: any;
  BASE_URL = ConfigService.app.baseUri;

  constructor(
    private apiService: ApiService,
    private translate: TranslateService,
    private helper: Helper,
    private httpClient: HttpClient,
    private oAuth: OAuthService
  ) {
    this.apiService.init();
    this.requestHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.oAuth.getAccessToken()}`
    }
  }

  getPropertiesAndTypes(type: string): Observable<[IProperty[], IPropertyType[]]> {
    return Observable.forkJoin(
      this.getProperties(type, 'isExtended eq true'),
      this.getPropertyTypes()
    );
  }

  getProperties(type: string, filter: string): Observable<IProperty[]> {
    return this.apiService.client.properties[type].GET(null, filter)
      .map((res) => {
        return this.apiService.handleResponse(res).results;
      });
  }

  getPropertyTypes(): Observable<IPropertyType[]> {
    return this.apiService.client.properties.types.GET()
      .map((res) => {
        return this.apiService.handleResponse(res).results;
      });
  }

  getPropertyValues(type: string, propertyId: number): Observable<PropertyValue.PropertyValue[]> {
    return this.apiService.client.properties[type].propertyId(propertyId.toString()).values.GET()
      .map((res) => {
        return this.apiService.handleResponse(res).results;
      });
  }

  saveProperty(type: string, id: number, property: PropertyPatch.PropertyPatch): Property.Property {
    return this.apiService.client.properties[type].propertyId(id.toString()).PATCH(property)
      .map((res) => {
        return this.apiService.handleResponse(res);
      });
  }

  savePropertyValue(type: string, value: PropertyValue.PropertyValue, oldValueId: string, propertyId: number): Observable<PropertyValue.PropertyValue> {
    return this.save(value, oldValueId, propertyId, this.apiService.client.properties[type]);
  }

  deletePropertyValue(type: string, valueId: string, propertyId: number): any {
    return this.apiService.client.properties[type].propertyId(propertyId.toString()).values.valueId(valueId).DELETE()
      .map((res) => {
        this.apiService.handleResponse(res);
        return (res.responseCode === 204);
      });
  }

  save(value: PropertyValue.PropertyValue, oldValueId: string, propertyId: number, service: any): Observable<PropertyValue.PropertyValue> {
    if (oldValueId === value.id) {
      // simple update
      return service.propertyId(propertyId).values.valueId(value.id).PATCH(value)
        .map((res) => {
          if (res.responseCode === 404) {
            return service.propertyId(propertyId).values.POST(value)
              .map((response) => response.bodyRaw);
          }
          return res.bodyRaw;
        });
    } else if (oldValueId === null) {
      // simple insert
      return service.propertyId(propertyId).values.POST(value)
        .map((res) => res.bodyRaw);
    } else {
      // attempt to update key
      return service.propertyId(propertyId).values.valueId(oldValueId).DELETE()
        .map((res) => {
          if (res.responseCode === 204) {
            return service.propertyId(propertyId).values.POST(value).map((response) => response.bodyRaw).subscribe();
          }
          return res.bodyRaw;
        });
    }
  }

  getPropertyTranslations(propertiesToTranslate: any[], propertyToUse, propertyToSet): Observable<any[]> {
    const translations = this.translate.translations[this.translate.currentLang];
    return forkJoin(this.getProperties('uc', 'isExtended eq true'), this.getProperties('so', 'isExtended eq true')).map(properties => {
      const allProperties = properties[0].concat(properties[1]).map(p => this.helper.toResourceKey(p.modelName));
      const translatableProperties = properties[0].concat(properties[1]).filter(p => p.modelName === p.displayName || translations[p.displayName] !== undefined).map(p => this.helper.toResourceKey(p.displayName));

      propertiesToTranslate.map(c => {
        if (c[propertyToUse]) {
        const resourceKey = this.helper.toResourceKey(c[propertyToUse]);
        const displayName = c['displayName'];
        if (allProperties.indexOf(resourceKey) === -1 || translatableProperties.indexOf(resourceKey) !== -1) {
          const translation = translations[resourceKey];
          if (translation !== undefined) {
            c[propertyToSet] = translation;
          } else {
            if (allProperties.indexOf(displayName) === -1 || translatableProperties.indexOf(displayName) !== -1) {
              const translationDisplayName = translations[displayName];
              if (translationDisplayName !== undefined) {
                c[propertyToSet] = translationDisplayName;
              }
            }
          }
        }
      }
      });
      return propertiesToTranslate;
    });
  }



  getPropertyTranslations2(propertiesToTranslate: any[], propertyToUse, propertyToSet): Observable<any[]> {
    const translations = this.translate.translations[this.translate.currentLang];

    propertiesToTranslate.map(c => {
      const resourceKey = (c[propertyToUse]);
      // const displayName = c['displayName'];
      const translation = translations[resourceKey];
      if (translation !== undefined) {
        c[propertyToSet] = translation;
      } else {
        c[propertyToSet] = resourceKey;
      }

    });
    return Observable.of(propertiesToTranslate);

  }

  getCustomSettings(): Observable<any> {
    const resourceUrl = `${this.BASE_URL}/settings/custom-settings`;
    return this.httpClient.get<any[]>(resourceUrl, { headers: new HttpHeaders(this.requestHeaders) });
  }

  editCustomSettingValue(customProperty: string, value: string): Observable<any> {
    return this.httpClient.patch(`${this.BASE_URL}/settings/custom-settings/${customProperty}`
      , JSON.stringify(value)
      , { headers: new HttpHeaders(this.requestHeaders) });
  }
}




// WEBPACK FOOTER //
// ./src/app/core/api/setting.service.ts